// Maps priorityScore value to ranking tier value
// This applies to Rule-based recommendation case
const mapToRankingTier = {
  '3.0': 'high',
  '2.0': 'middle',
  '1.0': 'low'
};

// Sanity check on score value before sending to FPTI:
// - Model confidence score range is -2 to 1 inclusive.
// - Intent-based apps has constant score of 1.5.
const isValidScoreRange = score => score >= -2.0 && score <= 3.0;

const getRankingTier = (rank, priorityScore) => {
  // If rankingTier exists, recommendation is rule-based, Else, it's model-based.
  // However, priorityScore of 1.0 could mean either Low rule OR confidence score of 1.0.
  // So, to distinguish check if rank = 1, then 1.0 is a confidence score.
  let rankingTier;
  if (
    (priorityScore !== '1.0' && mapToRankingTier[priorityScore]) ||
    (priorityScore === '1.0' && rank !== 1)
  ) {
    rankingTier = mapToRankingTier[priorityScore];
  }
  return rankingTier;
};

// Returns a stringified JSON object containing rank info about current app
// Used for rank field in individual Click and Impression events
// Rule-based e.g. "{\"app_key\":\"pp_masspay\",\"rank\":19,\"ranking_tier\":\"low\"}"
// Model-based e.g. "{\"app_key\":\"pp_masspay\",\"rank\":19,\"score\": 0.9999}"
const getRankJsonString = ({
  rank,
  appKey = '',
  priorityScore = '',
  categoryRank,
  sectionRank,
  intentRank
}) => {
  if (!rank) {
    return '';
  }

  const rankObject = {
    app_key: appKey,
    rank
  };

  // Add ranking_tier if it exists, else set score
  const rankingTier = getRankingTier(rank, priorityScore);
  if (rankingTier) {
    rankObject.ranking_tier = rankingTier;
  } else {
    const score = parseFloat(priorityScore);
    if (isValidScoreRange(score)) {
      rankObject.score = score;
    }
  }

  if (categoryRank) {
    rankObject.category_rank = categoryRank;
  }
  if (sectionRank) {
    rankObject.section_rank = sectionRank;
  }
  if (intentRank) {
    rankObject.intent_rank = intentRank;
  }
  return JSON.stringify(rankObject);
};

// This is used for rank field in Main Page or Category Page Impression event
// Helper function for creating app_ranking object, given an array of apps
// Rule-based e.g. "{\"payouts\":{\"app_key\":\"pp_masspay\",\"rank\":19,\"ranking_tier\":\"low\",\"category\":"accept_payments"`}}"
// Model-based e.g. replace "ranking_tier" by "score" data.
const getAppRankingObj = ({
  appArray = [],
  isCategoryPage = false,
  isRecommended = false
}) => {
  const appRankingObj = {};
  appArray.forEach(
    ({ appFPTI, appKey, rank, priorityScore, categoryKey, sectionKey }) => {
      if (!appFPTI || !rank) {
        return;
      }
      appRankingObj[appFPTI] = {
        app_key: appKey,
        rank
      };

      // Add ranking_tier if it exists, else set score
      const rankingTier = getRankingTier(rank, priorityScore);
      if (rankingTier) {
        appRankingObj[appFPTI].ranking_tier = rankingTier;
      } else {
        const score = parseFloat(priorityScore);
        if (isValidScoreRange(score)) {
          appRankingObj[appFPTI].score = score;
        }
      }

      // If on Main page, add app category data
      if (!isCategoryPage && isRecommended) {
        appRankingObj[appFPTI].category = 'recommended_for_you';
      } else if (!isCategoryPage && categoryKey) {
        appRankingObj[appFPTI].category = categoryKey;
      }
      // If on Category page and section exists, add app section data
      if (isCategoryPage && sectionKey) {
        appRankingObj[appFPTI].section = sectionKey;
      }
    }
  );
  return appRankingObj;
};

/* This is used for rank field in Main Page Impression event.
 * Process recommendedData and PS structuredData to create a "snapshot" of Main page:
 * app_ranking object with rank info for recommended and featured apps
 * and category_ranking object.
 * e.g. "{"app_ranking":{
 * "paypal_me":{"app_key":"pp_me","rank":1,"ranking_tier":"high","category":"recommended_for_you"},
 * "payouts":{"app_key":"pp_masspay","rank":2,"ranking_tier":"high","category":"recommended_for_you"},
 * "bdmc":{"app_key":"pp_bdmc","rank":4,"ranking_tier":"low","category":"accept_payments"}},
 * "category_ranking":{"recommended_for_you":{"rank":0},"accept_payments":{"rank":1}}"
 */
const buildMainAggRankJsonString = (
  recommendedApps,
  { categories = [], intentToolsDisplayed = [] } = {}
) => {
  // Hard-code first category ranking depending on experience
  const category_ranking = intentToolsDisplayed.length
    ? { we_think_you_might_like: { rank: 0 } }
    : { recommended_for_you: { rank: 0 } };
  const aggregatedRankObj = {
    app_ranking: {},
    category_ranking
  };
  const featuredApps = [];
  categories.forEach(({ featured = [], content = {} }) => {
    featuredApps.push(...featured);
    aggregatedRankObj.category_ranking[content.categoryFPTI] = {
      rank: content.rank
    };
  });
  // Add recommended apps data and then category-featured apps data
  aggregatedRankObj.app_ranking = getAppRankingObj({
    appArray: recommendedApps,
    isRecommended: true
  });
  aggregatedRankObj.app_ranking = {
    ...aggregatedRankObj.app_ranking,
    ...getAppRankingObj({ appArray: featuredApps })
  };
  return JSON.stringify(aggregatedRankObj);
};

/* This is used for rank field in Category Page Impression event.
 * Find current category's data from structuredData and use that to
 * build app_ranking and section_ranking objects.
 * e.g. "{"app_ranking":{
 * "payouts":{"app_key":"pp_masspay","rank":2,"ranking_tier":"high"},
 * "send_money":{"app_key":"pp_send_money","rank":20,"ranking_tier":"middle"},
 * "bdmc":{"app_key":"pp_bdmc","rank":26,"ranking_tier":"low"}},
 * "section_ranking":{}}"
 */
const buildCategoryAggRankJsonString = (
  currentCategory = {},
  { categories = [] } = {}
) => {
  const currentCategoryData =
    categories.find(
      ({ content }) => content.categoryKey === currentCategory.categoryKey
    ) || {};
  const { sections, tools = [] } = currentCategoryData;
  const aggregatedRankObj = {
    app_ranking: {},
    section_ranking: {}
  };
  const categoryApps = [...tools];
  if (Array.isArray(sections)) {
    sections.forEach(({ tools = [], content = {} }) => {
      categoryApps.push(...tools);
      // use sectionKey instead of sectionFPTI for easier mapping with appRankingObj
      aggregatedRankObj.section_ranking[content.sectionKey] = {
        rank: content.rank
      };
    });
  }
  aggregatedRankObj.app_ranking = getAppRankingObj({
    appArray: categoryApps,
    isCategoryPage: true
  });
  return JSON.stringify(aggregatedRankObj);
};

export {
  getRankJsonString,
  buildMainAggRankJsonString,
  buildCategoryAggRankJsonString
};
